// src/pages/pmc/EngineerMeasurementSheet.js
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const toNum = (v) =>
  Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

/**
 * PMC Engineer Measurement Sheet
 * Same layout as ContractorMeasurementSheetPage
 * Data stored in: tenders/{tenderId}/measurements_engineer/{itemNo}
 */
export default function EngineerMeasurementSheet() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [tender, setTender] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [measurements, setMeasurements] = useState({});
  const [selectedItemNo, setSelectedItemNo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemTemplates, setItemTemplates] = useState({}); // Excel layout

  /* ---------- AUTH (Engineer login) ---------- */
useEffect(() => {
  const unsub = onAuthStateChanged(auth, (u) => {
    setUser(u || null);
    setAuthReady(true);

    if (!u) {
      toast.error("Please login as Engineer.");
      navigate("/tender/engineer");
      return; // ✅ ADD THIS (important)
    }
  });
  return () => unsub();
}, [navigate]);


  /* ---------- LOAD TENDER + BOQ + ENGINEER MEASUREMENTS + TEMPLATE ---------- */
  useEffect(() => {
    if (!user || !tenderId) return;

    (async () => {
      try {
        setLoading(true);

        // Tender
        const tSnap = await getDoc(doc(db, "tenders", tenderId));
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          return;
        }
        const t = { id: tSnap.id, ...tSnap.data() };
        setTender(t);

        // BOQ
        const boqSnap = await getDocs(
          collection(db, "tenders", tenderId, "boq_items")
        );
        const list = [];
        boqSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));
        setBoqItems(list);
        if (list.length) setSelectedItemNo(list[0].itemNo);

        // ENGINEER measurements
        const measSnap = await getDocs(
          collection(db, "tenders", tenderId, "measurements_engineer")
        );
        const m = {};
        measSnap.forEach((d) => {
          const data = d.data();
          if (data.itemNo != null) m[data.itemNo] = { id: d.id, ...data };
        });
        setMeasurements(m);

        // FULL sheet template (same as contractor)
        const msSnap = await getDoc(
          doc(
            db,
            "tenders",
            tenderId,
            "measurement_sheets",
            "FULL_MEASUREMENT_SHEET"
          )
        );
        if (msSnap.exists()) {
          const data = msSnap.data();
          const rows = Array.isArray(data.rows) ? data.rows : [];
          const templates = {};
          let currentItemNo = null;
          rows.forEach((r) => {
            const itemNoCell =
              r["ITEM NO"] ??
              r["Item No."] ??
              r["Item No"] ??
              r["Item"] ??
              r["ITEM"];
            if (itemNoCell !== undefined && itemNoCell !== "") {
              const n = Number(itemNoCell);
              if (!isNaN(n) && n > 0) currentItemNo = n;
            }
            if (!currentItemNo) return;
            if (!templates[currentItemNo]) templates[currentItemNo] = [];
            templates[currentItemNo].push(r);
          });
          setItemTemplates(templates);
        } else {
          setItemTemplates({});
        }
      } catch (e) {
        console.error(e);
        toast.error("Error loading engineer measurement sheet.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, tenderId, navigate]);

  if (!user) {
  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen flex items-center justify-center bg-emerald-50">
        <div className="text-sm text-slate-600">Checking login…</div>
      </div>
    </>
  );
}

if (loading) {
  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen flex items-center justify-center bg-emerald-50">
        <div className="text-sm text-slate-600">Loading engineer view…</div>
      </div>
    </>
  );
}

if (!tender) {
  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen flex items-center justify-center bg-emerald-50">
        <div className="text-sm text-red-600">
          Tender not found or you do not have permission to view it.
        </div>
      </div>
    </>
  );
}

  const selectedBoq = boqItems.find((b) => b.itemNo === selectedItemNo);
  const templateRowsForItem =
    (selectedItemNo && itemTemplates[selectedItemNo]) || null;

  return (
    <div className="min-h-screen bg-emerald-50 pb-10">
      <Toaster position="top-center" />
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* HEADER */}
        <div className="p-4 rounded-2xl bg-white shadow-lg border border-emerald-200 flex justify-between items-start mb-6">
          <div>
            <div className="text-xs text-gray-500 uppercase">
              PMC Engineer – Measurement Sheet
            </div>
            <div className="text-lg md:text-xl font-bold text-emerald-800">
              {tender.title || tender.projectName || "Project"}
            </div>
            <div className="text-[11px] text-slate-600">
              {tender.ownerCompany || ""} ·{" "}
              {tender.city || tender.district || ""} ·{" "}
              {tender.state || "Gujarat"}
            </div>
          </div>
          <div className="text-right text-[11px]">
            <div>User: {user.email}</div>
            <div>Date: {dayjs().format("DD/MM/YYYY")}</div>
            <button
              onClick={() => navigate("/pmc/dashboard")}
              className="mt-2 text-emerald-700 underline"
            >
              ← Back to PMC Dashboard
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white p-4 rounded-xl shadow border text-[12px]">
            Loading BOQ & engineer measurements…
          </div>
        ) : boqItems.length === 0 ? (
          <div className="bg-white p-4 rounded-xl shadow border text-[12px]">
            BOQ items not found.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {/* LEFT – BOQ list */}
            <div className="bg-white p-3 rounded-xl shadow border overflow-auto max-h-[520px]">
              <div className="text-xs font-semibold text-emerald-700 mb-2">
                BOQ Items (Engineer)
              </div>
              {boqItems.map((b) => {
                const meas = measurements[b.itemNo];
                const prevTotal = toNum(meas?.engineerTotalQty);
                const boqQty = toNum(b.boqQty);
                const bal = boqQty - prevTotal;
                return (
                  <button
                    key={b.id}
                    onClick={() => setSelectedItemNo(b.itemNo)}
                    className={cx(
                      "w-full text-left p-2 rounded-lg border mb-2 text-[11px]",
                      selectedItemNo === b.itemNo
                        ? "bg-emerald-50 border-emerald-500"
                        : "bg-white border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    <div className="font-semibold">
                      #{b.itemNo} – {b.unit || ""}
                    </div>
                    <div className="line-clamp-2 text-slate-600">
                      {b.description}
                    </div>
                    <div className="mt-1 text-[10px] text-slate-700">
                      BOQ: {boqQty} · Eng Measured:{" "}
                      {prevTotal ? prevTotal.toFixed(3) : "0"} · Bal:{" "}
                      {bal.toFixed(3)}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* RIGHT – Measurement form */}
            <div className="col-span-2 bg-white p-3 rounded-xl shadow border">
              {!selectedBoq ? (
                <div className="text-[12px] text-slate-600">
                  Select any BOQ item from left to enter engineer measurement.
                </div>
              ) : (
                <EngineerMeasurementForm
                  tender={tender}
                  tenderId={tenderId}
                  user={user}
                  boqItem={selectedBoq}
                  existing={measurements[selectedBoq.itemNo]}
                  templateRows={templateRowsForItem}
                  onSaved={(d) =>
                    setMeasurements((prev) => ({
                      ...prev,
                      [d.itemNo]: d,
                    }))
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ENGINEER SINGLE ITEM FORM
   (same logic as contractor, separate collection)
   ============================================================ */

function EngineerMeasurementForm({
  tender,
  tenderId,
  user,
  boqItem,
  existing,
  templateRows,
  onSaved,
}) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(!existing);

  // template detail rows (exclude description row)
  const templateDetailRows =
    Array.isArray(templateRows) && templateRows.length > 1
      ? templateRows.slice(1)
      : [];

  // helper blank row with default label
  const createBlankRow = (idx, defaultLabel = "") => ({
    id: `eng-row-${boqItem.itemNo}-${idx}-${Date.now()}`,
    label: defaultLabel,
    nos: "",
    length: "",
    width: "",
    depth: "",
    qty: "",
    photos: [],
  });

  // prefix for invoice
  const invPrefix = `ITEM${boqItem.itemNo}/ENG`;

  useEffect(() => {
    if (templateDetailRows.length > 0) {
      const blankRows = templateDetailRows.map((r, idx) => {
        const label =
          (r["ITEM DESCRIPTION"] ||
            r["Item Description"] ||
            r["Description"] ||
            "").toString();
        return createBlankRow(idx, label);
      });
      setRows(blankRows);
    } else {
      setRows([createBlankRow(0)]);
    }
  }, [boqItem.itemNo, templateRows]);

  const handleRowChange = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, [field]: value };
        const n = toNum(next.nos);
        const L = toNum(next.length);
        const W = toNum(next.width);
        const D = toNum(next.depth);
        if (n && L && W && D) {
          next.qty = (n * L * W * D).toFixed(3);
        }
        return next;
      })
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createBlankRow(prev.length)]);
  };

  const handlePhotoChange = async (idx, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const row = rows[idx];
      const path = `tender_measurements_engineer/${tenderId}/${boqItem.itemNo}/${row.id}/${Date.now()}_${file.name}`;
      const sRef = ref(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      setRows((prev) =>
        prev.map((r, i) =>
          i === idx
            ? {
                ...r,
                photos: [
                  ...(Array.isArray(r.photos) ? r.photos : []),
                  { url, path, name: file.name },
                ],
              }
            : r
        )
      );
      toast.success("Photo uploaded (Engineer)");
    } catch (e) {
      console.error(e);
      toast.error("Engineer photo upload failed");
    }
  };

  // BOQ limit check
  const boqQty = toNum(boqItem.boqQty);
  const prevTotal = toNum(existing?.engineerTotalQty || 0);
  const thisSheetQty = rows.reduce(
    (sum, r) => sum + toNum(r.qty),
    0
  );
  const aggregatedTotal = prevTotal + thisSheetQty;
  const remainingAfter = boqQty - aggregatedTotal;
  const overLimit = aggregatedTotal - boqQty > 0.0001;

  const handleSave = async () => {
    if (!rows.length) {
      toast.error("Engineer: add at least one row.");
      return;
    }
    if (thisSheetQty <= 0) {
      toast.error("Engineer: measurement qty is 0.");
      return;
    }
    if (overLimit) {
      toast.error(
        "Engineer: total measurement exceeds BOQ quantity."
      );
      return;
    }

    try {
      setSaving(true);
      const docId = String(boqItem.itemNo);

      const existingInvoiceNo = existing?.sheetInvoiceNo;
      const sheetSeq = 1; // per item 1 sheet (aage extend kar sakte)
      const generatedInvoiceNo = `${invPrefix}/MSHEET/${String(
        sheetSeq
      ).padStart(4, "0")}`;
      const invoiceNo = existingInvoiceNo || generatedInvoiceNo;
      const sheetDate =
        existing?.sheetDate || dayjs().format("YYYY-MM-DD");

      const newTotal = aggregatedTotal;

      const payload = {
        tenderId,
        itemNo: boqItem.itemNo,
        boqId: boqItem.id,
        unit: boqItem.unit || "",
        boqQty,
        engineerTotalQty: newTotal,
        rows,
        createdByUid: existing?.createdByUid || user.uid,
        createdAt: existing?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: existing?.status || "draft",
        lockedByCeo: existing?.lockedByCeo || false,
        sheetInvoiceNo: invoiceNo,
        sheetDate,
      };

      await setDoc(
        doc(
          db,
          "tenders",
          tenderId,
          "measurements_engineer",
          docId
        ),
        payload,
        { merge: true }
      );

      toast.success("Engineer measurement saved.");
      onSaved && onSaved(payload);
      setIsEditing(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save engineer measurement.");
    } finally {
      setSaving(false);
    }
  };

  // PDF header details
  const savedInvoiceNo = existing?.sheetInvoiceNo;
  const savedDate =
    existing?.sheetDate ||
    (existing?.updatedAt?.toDate
      ? dayjs(existing.updatedAt.toDate()).format("DD/MM/YYYY")
      : "");

  const readOnlyFromTemplate = (r) => ({
    label:
      r["ITEM DESCRIPTION"] ||
      r["Item Description"] ||
      r["Description"] ||
      "",
    nos:
      r["NOS"] || r["Nos"] || r["Nos."] || r["NOS "] || "",
    length: r["LENGTH"] || r["Length"] || "",
    width: r["WIDTH"] || r["Width"] || "",
    depth: r["DEPTH"] || r["Depth"] || "",
    qty: r["QTY"] || r["Quantity"] || r["QUANTITY"] || "",
  });


  return (
    <div className="space-y-4 text-[11px]">
      {/* header card (item info) */}
      <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
        <div className="flex justify-between">
          <div>
            <div className="font-semibold text-slate-800">
              Item #{boqItem.itemNo} – {boqItem.unit || ""}
            </div>
            <div className="text-[10px] text-slate-700">
              {boqItem.description}
            </div>
          </div>
          <div className="text-right text-[10px] text-slate-700">
            <div>BOQ Qty: {boqQty.toFixed(3)}</div>
            <div>
              Engineer Qty (previous):{" "}
              {prevTotal ? prevTotal.toFixed(3) : "0.000"}
            </div>
            <div>
              This sheet qty:{" "}
              {thisSheetQty ? thisSheetQty.toFixed(3) : "0.000"}
            </div>
            <div className="font-semibold">
              Total after save:{" "}
              {aggregatedTotal ? aggregatedTotal.toFixed(3) : "0.000"}
            </div>
            <div className={overLimit ? "text-red-600" : "text-emerald-700"}>
              Balance after save: {Math.max(remainingAfter, 0).toFixed(3)}
            </div>
          </div>
        </div>
      </div>

      {/* SYSTEM LAYOUT (Excel) */}
      {templateDetailRows.length > 0 && (
        <div className="border border-slate-300 rounded-xl overflow-auto">
          <div className="px-2 py-1 bg-slate-100 text-[10px] font-semibold text-slate-700">
            System Measurement Layout (PMC Excel – Read Only)
          </div>
          <table className="min-w-full text-[10px] border-collapse">
            <thead className="bg-slate-100">
              <tr>
                <th className="border border-slate-300 px-2 py-1 text-left">
                  Label / Description
                </th>
                <th className="border border-slate-300 px-2 py-1">Nos</th>
                <th className="border border-slate-300 px-2 py-1">
                  Length
                </th>
                <th className="border border-slate-300 px-2 py-1">
                  Width
                </th>
                <th className="border border-slate-300 px-2 py-1">
                  Depth
                </th>
                <th className="border border-slate-300 px-2 py-1">
                  Qty
                </th>
              </tr>
            </thead>
            <tbody>
              {templateDetailRows.map((row, idx) => {
                const r = readOnlyFromTemplate(row);
                return (
                  <tr
                    key={`sys-${idx}`}
                    className="odd:bg-white even:bg-slate-50"
                  >
                    <td className="border border-slate-300 px-2 py-1">
                      {r.label}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-right">
                      {r.nos}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-right">
                      {r.length}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-right">
                      {r.width}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-right">
                      {r.depth}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-right">
                      {r.qty}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ENTRY FORM (only when editing) */}
      {isEditing && (
        <>
          <div className="border border-slate-300 rounded-xl overflow-auto">
            <div className="px-2 py-1 bg-emerald-50 text-[10px] font-semibold text-emerald-800">
              Engineer Measurement Entry
            </div>
            <table className="min-w-full text-[10px] border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border border-slate-300 px-2 py-1 text-left">
                    Label / Description
                  </th>
                  <th className="border border-slate-300 px-2 py-1">Nos</th>
                  <th className="border border-slate-300 px-2 py-1">
                    Length
                  </th>
                  <th className="border border-slate-300 px-2 py-1">
                    Width
                  </th>
                  <th className="border border-slate-300 px-2 py-1">
                    Depth
                  </th>
                  <th className="border border-slate-300 px-2 py-1">
                    Qty
                  </th>
                  <th className="border border-slate-300 px-2 py-1">
                    Photo
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={r.id}
                    className="odd:bg-white even:bg-slate-50"
                  >
                    <td className="border border-slate-300 px-2 py-1">
                      <input
                        value={r.label}
                        onChange={(e) =>
                          handleRowChange(idx, "label", e.target.value)
                        }
                        className="w-full border border-slate-300 rounded px-1 py-0.5"
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.nos}
                        onChange={(e) =>
                          handleRowChange(idx, "nos", e.target.value)
                        }
                        className="w-full border border-slate-300 rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.length}
                        onChange={(e) =>
                          handleRowChange(idx, "length", e.target.value)
                        }
                        className="w-full border border-slate-300 rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.width}
                        onChange={(e) =>
                          handleRowChange(idx, "width", e.target.value)
                        }
                        className="w-full border border-slate-300 rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.depth}
                        onChange={(e) =>
                          handleRowChange(idx, "depth", e.target.value)
                        }
                        className="w-full border border-slate-300 rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.qty}
                        onChange={(e) =>
                          handleRowChange(idx, "qty", e.target.value)
                        }
                        className="w-full border border-slate-300 rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoChange(idx, e)}
                        className="text-[9px]"
                      />
                      {Array.isArray(r.photos) && r.photos.length > 0 && (
                        <div className="mt-1 text-[9px] text-emerald-700 underline cursor-pointer">
                          {r.photos.length} photo(s)
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold">
                  <td
                    className="border border-slate-300 px-2 py-1 text-right"
                    colSpan={5}
                  >
                    This sheet total
                  </td>
                  <td className="border border-slate-300 px-2 py-1 text-right">
                    {thisSheetQty.toFixed(3)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1" />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex justify-between items-center mt-2">
            <button
              type="button"
              onClick={handleAddRow}
              className="px-3 py-1.5 rounded-lg border text-[11px] hover:bg-slate-50"
            >
              + Add Row
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || overLimit}
              className={cx(
                "px-4 py-1.5 rounded-lg text-[11px] font-semibold text-white",
                saving || overLimit
                  ? "bg-emerald-300 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700"
              )}
            >
              {saving ? "Saving…" : "Save Engineer Measurement"}
            </button>
          </div>
        </>
      )}

      {/* SAVED SHEET LIST (1 row) */}
      <div className="mt-4 border border-slate-300 rounded-xl overflow-hidden">
        <div className="px-3 py-2 bg-slate-900 text-[10px] text-white font-semibold flex justify-between">
          <span>Saved Engineer Measurement Sheet</span>
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-2 py-0.5 rounded bg-white text-slate-900"
            >
              Edit / New Sheet
            </button>
          )}
        </div>
        <table className="min-w-full text-[10px] border-collapse">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-300 px-2 py-1">Sr</th>
              <th className="border border-slate-300 px-2 py-1 text-left">
                Date
              </th>
              <th className="border border-slate-300 px-2 py-1 text-left">
                Invoice No
              </th>
              <th className="border border-slate-300 px-2 py-1">
                Download
              </th>
            </tr>
          </thead>
          <tbody>
            {existing ? (
              <tr className="odd:bg-white even:bg-slate-50">
                <td className="border border-slate-300 px-2 py-1 text-center">
                  1
                </td>
                <td className="border border-slate-300 px-2 py-1">
                  {savedDate || "—"}
                </td>
                <td className="border border-slate-300 px-2 py-1">
                  {savedInvoiceNo || "—"}
                </td>
                <td className="border border-slate-300 px-2 py-1 text-center">
                  <button
                    type="button"
                    onClick={() =>
                      generateEngineerMeasurementPdf(
                        tender,
                        boqItem,
                        existing
                      )
                    }
                    className="px-2 py-0.5 rounded bg-slate-900 text-white"
                  >
                    PDF
                  </button>
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="border border-slate-300 px-2 py-2 text-center text-slate-500"
                >
                  No engineer sheet saved yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Engineer Measurement PDF (same style as contractor) ---------- */
function generateEngineerMeasurementPdf(tender, boqItem, sheet) {
  try {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;

    const title =
      tender?.title || tender?.projectName || "Measurement Sheet (Engineer)";
    const pacsName =
      tender?.ownerCompany ||
      tender?.pacsName ||
      tender?.pacsShortName ||
      "";
    const invoiceNo =
      sheet.sheetInvoiceNo || "ENG/MSHEET/0001";
    const sheetDate =
      sheet.sheetDate ||
      (sheet.updatedAt?.toDate
        ? dayjs(sheet.updatedAt.toDate()).format("DD/MM/YYYY")
        : dayjs().format("DD/MM/YYYY"));

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Engineer Measurement Sheet", pageWidth / 2, 15, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const titleLines = doc.splitTextToSize(title, contentWidth);
    doc.text(titleLines, marginLeft, 22);
    let y = 22 + titleLines.length * 4;

    if (pacsName) {
      doc.text(`PACS: ${pacsName}`, marginLeft, y);
      y += 4;
    }

    doc.text(`Invoice No: ${invoiceNo}`, marginLeft, y);
    doc.text(`Date: ${sheetDate}`, pageWidth - marginRight, y, {
      align: "right",
    });
    y += 6;

    doc.text(
      `Item #${boqItem.itemNo} – ${boqItem.unit || ""}`,
      marginLeft,
      y
    );
    y += 4;
    const descLines = doc.splitTextToSize(
      boqItem.description || "",
      contentWidth
    );
    doc.text(descLines, marginLeft, y);
    y += descLines.length * 4 + 3;

    const validRows = (sheet.rows || []).filter((r) => {
      return (
        (r.label && String(r.label).trim() !== "") ||
        toNum(r.nos) ||
        toNum(r.length) ||
        toNum(r.width) ||
        toNum(r.depth) ||
        toNum(r.qty)
      );
    });

    const body = validRows.map((r, idx) => [
      idx + 1,
      r.label || "",
      r.nos || "",
      r.length || "",
      r.width || "",
      r.depth || "",
      r.qty || "",
    ]);

    autoTable(doc, {
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      head: [
        [
          "Sr",
          "Label / Description",
          "Nos",
          "Length",
          "Width",
          "Depth",
          "Qty",
        ],
      ],
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
        lineWidth: 0.25,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 70 },
        2: { cellWidth: 12, halign: "right" },
        3: { cellWidth: 14, halign: "right" },
        4: { cellWidth: 14, halign: "right" },
        5: { cellWidth: 14, halign: "right" },
        6: { cellWidth: 18, halign: "right" },
      },
    });

    const tableEndY = doc.lastAutoTable.finalY || y + 40;
    const totalQty = (sheet.engineerTotalQty || 0).toFixed(3);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(
      `Total Measured Qty (Engineer): ${totalQty} ${boqItem.unit || ""}`,
      pageWidth - marginRight,
      tableEndY + 8,
      { align: "right" }
    );

    const sigY = tableEndY + 22;
    const contentWidth2 = contentWidth;
    const sigWidth = (contentWidth2 - 20) / 2;
    const x1 = marginLeft;
    const x2 = marginLeft + sigWidth + 10;

    doc.setLineWidth(0.4);
    doc.line(x1, sigY, x1 + sigWidth, sigY);
    doc.line(x2, sigY, x2 + sigWidth, sigY);

    doc.setFontSize(8);
    doc.text(
      "Contractor",
      x1 + sigWidth / 2,
      sigY + 4,
      { align: "center" }
    );
    doc.text(
      "PMC Engineer",
      x2 + sigWidth / 2,
      sigY + 4,
      { align: "center" }
    );

    doc.save(`${invoiceNo.replace(/[\/]/g, "_")}.pdf`);
  } catch (e) {
    console.error(e);
    toast.error("Engineer measurement PDF error.");
  }
}
